import React, { FC, useState } from "react";
import { Character } from "@models/character";
import { Box, Fab } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import styled from "@emotion/styled";
import { Person } from "@models/person";
import { useTranslation } from "react-i18next";
import PhotoPlaceholder from "@components/PhotoPlaceholder";
import StyledLink from "@components/StyledLink/StyledLink";
import { formatNameWithRelation } from "@utils/formatting.util";

const ImageContainer = styled.div`
  height: 108px;
  width: 108px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  height: 100%;
  width: 100%;
  object-fit: cover;
`;

const RemoveButton = styled(Fab)`
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translate(-50%, 50%);
  z-index: 1;
`;

const StyledCheckbox = styled.input`
  position: absolute;
  top: 2px;
  right: 2px;
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

export interface CharacterItemProps {
  person: Person;
  character?: Character;
  onRemove?: () => void;
  onChange: () => void;
  onCheck?: () => void;
  onSelect?: () => void;
  checked?: boolean;
}

const CharacterItem: FC<CharacterItemProps> = ({
  person,
  character,
  onRemove,
  onChange,
  onCheck,
  onSelect,
  checked,
}) => {
  const { t } = useTranslation();
  const [removeButtonVisible, setRemoveButtonVisible] = useState(false);

  return (
    <Box maxWidth="128px">
      <ImageContainer
        onMouseEnter={() => setRemoveButtonVisible(true)}
        onMouseLeave={() => setRemoveButtonVisible(false)}
        onClick={() => onSelect?.()}
      >
        {onRemove && removeButtonVisible && (
          <RemoveButton color="error" onClick={onRemove} size="small">
            <DeleteOutlineOutlinedIcon />
          </RemoveButton>
        )}
        {onCheck && (
          <StyledCheckbox
            onChange={onCheck}
            checked={checked}
            type="checkbox"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {character?.mainFace ? (
          <Image
            src={
              character?.mainFace.faceUrl ||
              process.env.PUBLIC_URL + "/images/file-placeholder.png"
            }
          />
        ) : (
          <PhotoPlaceholder width="100%" height="100%" />
        )}
      </ImageContainer>

      <Box
        marginTop={onRemove ? "12px" : "8px"}
        overflow="hidden"
        textOverflow="ellipsis"
      >
        <StyledLink to="" onClick={onChange}>
          {formatNameWithRelation(person) || t("characters.item.selectButton")}
        </StyledLink>
      </Box>
    </Box>
  );
};

export default CharacterItem;
